/**
 * /matches/new — admin-injected match creation form (PR-3 Task 22).
 *
 * Form fields:
 *   - user1_id (uuid)
 *   - user2_id (uuid)
 *   - reason (required textarea — recorded in audit + admin_create_reason)
 *   - force_dealbreakers (checkbox; bypass the 7-rule safety gate)
 *   - trigger_summary (checkbox; default ON)
 *
 * Permission gate (route): `matches.create`.
 *
 * Each entered id also shows, under its field (PoolTierNote, fed by
 * GET /v1/admin/matches/pool-tiers):
 *   - that member's MATCH-POOL TIER. An admin-created match ignores the tier on
 *     purpose, so that line DISCLOSES and never blocks — see the component and
 *     the header of backend/src/services/admin-match-create.ts; and
 *   - whether THE CREATE WILL ACCEPT the member, from the create's own
 *     eligibility predicate (2026-10-30). The tier used to be the only thing
 *     disclosed, so the screen read "Active" for members the server was certain
 *     to refuse, and the operator found out only after clicking.
 *
 * Both are disclosures. `canSubmit` consults NEITHER: the server is the one
 * authority on a refusal, and it returns the reason the operator needs.
 *
 * Backend contract:
 *   POST /v1/admin/matches
 *     body { user1_id, user2_id, reason, force_dealbreakers?, trigger_summary? }
 *   400 → validation, 409 → already exists, 422 → safety violation.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.6 / §6.9
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApiUrl } from '@refinedev/core';
import { ChevronLeft } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { PoolTierNote, type PoolTierLookup } from './components/PoolTierNote';
import type { MatchPoolTier } from '@/lib/match-pool-tier';

export default function MatchNewPage() {
  const apiUrl = useApiUrl();
  const nav = useNavigate();
  const [user1Id, setUser1Id] = useState('');
  const [user2Id, setUser2Id] = useState('');
  const [reason, setReason] = useState('');
  const [forceDB, setForceDB] = useState(false);
  const [triggerSummary, setTriggerSummary] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [errStatus, setErrStatus] = useState<number | null>(null);

  // Match-pool tier per entered id. The ref is the cache (one lookup per id,
  // whatever the operator retypes); the state is what renders. A failed
  // lookup stays 'failed' rather than retrying on every keystroke — the
  // operator reloads, and 'failed' never reads as 'active'.
  const lookedUp = useRef<Record<string, PoolTierLookup>>({});
  const [lookups, setLookups] = useState<Record<string, PoolTierLookup>>({});
  // 🔴 The ONLY reason a lookup is abandoned (2026-10-30). It used to be
  // abandoned by the effect's own cleanup, and that cleanup runs on EVERY
  // keystroke in the other field, because both ids are in the dependency
  // list. So the normal act of typing the second id killed the first id's
  // request in flight. Its entry was already 'loading' in the cache, which is
  // no longer `undefined`, so no later run ever retried it: the line sat on
  // "Match pool: checking…" for as long as the screen stayed open.
  //
  // Measured on PREDEV at 15:58Z: entered alone, an id resolves in under six
  // seconds; entered first and followed by typing in the other field, it never
  // resolved. The race existed from the start (#3676) and became reliable when
  // #3678 added the matchability verdict, which roughly tripled the lookup's
  // work. A cancel-on-dependency-change is correct for a search box whose
  // query changes; here each id is fetched once and written to its own cache
  // key, so a late answer cannot overwrite a newer one and there is nothing to
  // cancel. Unmount is the only thing that must stop the write.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    const ids = [
      ...new Set([user1Id, user2Id].map(v => v.trim().toLowerCase()).filter(isUuid)),
    ].filter(id => lookedUp.current[id] === undefined);
    if (ids.length === 0) return;

    for (const id of ids) lookedUp.current[id] = { state: 'loading' };
    setLookups({ ...lookedUp.current });

    void (async () => {
      try {
        const r = await adminFetch(
          `${apiUrl}${POOL_TIERS_PATH}?user_ids=${encodeURIComponent(ids.join(','))}`,
        );
        const data = (await r.json()) as {
          tiers?: {
            user_id: string;
            tier: MatchPoolTier;
            unopened_streak: number;
            // Absent from an API that predates the verdict (the panel and the
            // API ship in one workflow, not in one instant). `?? null` below
            // keeps that state as "not known" and never as a pass.
            matchable?: boolean | null;
            blocked_reason?: string | null;
          }[];
          missing?: string[];
        };
        for (const t of data.tiers ?? []) {
          lookedUp.current[t.user_id] = {
            state: 'known',
            tier: t.tier,
            unopened_streak: t.unopened_streak,
            matchable: t.matchable ?? null,
            blocked_reason: t.blocked_reason ?? null,
          };
        }
        for (const id of data.missing ?? []) lookedUp.current[id] = { state: 'missing' };
        // An id the response answered about NEITHER way is unknown, not active.
        for (const id of ids) {
          if (lookedUp.current[id]?.state === 'loading') {
            lookedUp.current[id] = { state: 'failed' };
          }
        }
      } catch {
        for (const id of ids) lookedUp.current[id] = { state: 'failed' };
      }
      // The cache is a ref shared by every run, and each id is written to its
      // own key, so two lookups in flight cannot lose each other's answer.
      if (mounted.current) setLookups({ ...lookedUp.current });
    })();
  }, [user1Id, user2Id, apiUrl]);

  /** The tier line's state for one field's current text. */
  const lookupFor = (v: string): PoolTierLookup =>
    isUuid(v) ? (lookups[v.trim().toLowerCase()] ?? { state: 'loading' }) : { state: 'idle' };

  // Surfaces env to the operator so prod admin-creates are visibly riskier.
  // VITE_ENV is set by CI; defaults to dev.
  const isProd =
    (import.meta as ImportMeta & { env?: { VITE_ENV?: string } }).env
      ?.VITE_ENV === 'prod';

  const canSubmit = isUuid(user1Id) && isUuid(user2Id) && reason.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    if (
      forceDB &&
      !window.confirm(
        'Force-dealbreakers bypasses the gender / age / distance safety gates. Continue?',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    setErrStatus(null);
    try {
      const r = await adminFetch(`${apiUrl}/v1/admin/matches`, {
        method: 'POST',
        body: JSON.stringify({
          user1_id: user1Id.trim(),
          user2_id: user2Id.trim(),
          reason: reason.trim(),
          force_dealbreakers: forceDB,
          trigger_summary: triggerSummary,
        }),
      });
      const data = await r.json();
      if (data?.match_id) {
        nav(`/matches/${data.match_id}`);
      } else {
        nav('/matches');
      }
    } catch (e: unknown) {
      if (e instanceof AdminApiError) {
        setErr(e.message);
        setErrStatus(e.status);
      } else {
        setErr('Create match failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 640, margin: '0 auto' }}>
      <nav style={{ marginBottom: 12, fontSize: 13 }}>
        <Link
          to="/matches"
          className="inline-flex items-center gap-1 text-primary no-underline hover:underline"
        >
          <ChevronLeft size={14} /> Back to matches
        </Link>
      </nav>

      <h1 style={{ margin: '0 0 12px 0' }}>Create match (admin-injected)</h1>

      <div
        className={`mb-4 rounded-md border px-3 py-3 text-[13px] ${
          isProd
            ? 'border-destructive bg-destructive/10 text-destructive'
            : 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
        }`}
      >
        {isProd ? (
          <>
            <strong>⚠ ENVIRONMENT: PRODUCTION.</strong> This match will be
            visible to real customers and emits a paging metric
            (MatchCreatedByAdmin). Use only when explicitly authorized.
          </>
        ) : (
          <>
            <strong>⚠ ENVIRONMENT: DEV.</strong> Admin-created matches bypass
            the matching algorithm and skip the safety nets the backend
            normally enforces. Every create writes an audit row.
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="User A ID (UUID)">
          <input
            value={user1Id}
            onChange={e => setUser1Id(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className={inputClass}
            aria-invalid={user1Id.length > 0 && !isUuid(user1Id)}
          />
          {user1Id.length > 0 && !isUuid(user1Id) && (
            <FieldHint tone="warn">Not a valid UUID</FieldHint>
          )}
          <PoolTierNote lookup={lookupFor(user1Id)} />
        </Field>

        <Field label="User B ID (UUID)">
          <input
            value={user2Id}
            onChange={e => setUser2Id(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className={inputClass}
            aria-invalid={user2Id.length > 0 && !isUuid(user2Id)}
          />
          {user2Id.length > 0 && !isUuid(user2Id) && (
            <FieldHint tone="warn">Not a valid UUID</FieldHint>
          )}
          <PoolTierNote lookup={lookupFor(user2Id)} />
          {isUuid(user1Id) &&
            isUuid(user2Id) &&
            user1Id.trim() === user2Id.trim() && (
              <FieldHint tone="warn">User A and User B must differ</FieldHint>
            )}
        </Field>

        <Field label="Reason (required, audited)">
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Why is this match being injected? (e.g. friends-and-family beta pairing)"
            rows={3}
            className={inputClass}
            style={{ fontFamily: 'inherit', resize: 'vertical' }}
          />
        </Field>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={forceDB}
            onChange={e => setForceDB(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>Force dealbreakers</strong>
            <div className="mt-0.5 text-muted-foreground">
              Bypass the gender / age / distance safety gates. Backend still
              enforces 7 hard constraints. Use only when you understand the
              implications.
            </div>
          </span>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={triggerSummary}
            onChange={e => setTriggerSummary(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            Generate 6-pillar summaries after creation
            <div className="mt-0.5 text-muted-foreground">
              Default ON. Triggers the LLM generator for both users in en+es.
            </div>
          </span>
        </label>

        {err && (
          <div className="rounded-md bg-destructive/10 p-2.5 text-[13px] text-destructive">
            <strong>
              {errStatus === 409
                ? 'Conflict (409):'
                : errStatus === 422
                  ? 'Safety violation (422):'
                  : errStatus
                    ? `Error ${errStatus}:`
                    : 'Error:'}
            </strong>{' '}
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            onClick={() => nav('/matches')}
            type="button"
            className="cursor-pointer rounded-md border bg-background px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canSubmit || busy}
          >
            {busy ? 'Creating…' : 'Create match'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      {children}
    </label>
  );
}

function FieldHint({
  tone,
  children,
}: {
  tone: 'warn';
  children: React.ReactNode;
}) {
  return (
    <span
      className={`text-xs ${
        tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
      }`}
    >
      {children}
    </span>
  );
}

// Loose UUID v4-ish shape check. The backend re-validates; this is just
// inline UX so we don't submit obvious typos.
const UUID_RX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
function isUuid(v: string) {
  return UUID_RX.test(v.trim());
}

// A standalone literal on purpose. tests/unit/lint/admin-fetch-route-exists.test.ts
// is what proves this call has a backend route, and a path spelled only inside
// a template after the interpolated base URL was invisible to it until
// 2026-10-29. Keeping the path in its own const states the wire plainly and
// does not depend on that scanner staying wide.
const POOL_TIERS_PATH = '/v1/admin/matches/pool-tiers';

const inputClass =
  'rounded-md border bg-background px-2.5 py-2 text-sm text-foreground placeholder:text-muted-foreground';
